/**
 * PriceTraceTrigger (S9.2) — the SO-line price-trace affordance, now a THIN
 * wrapper over the shared `common/PriceTracePanel` (Drawer 480).
 *
 * Replaces the S2.2 inline PriceTracePopover: same dotted-underline price +
 * purple audit icon, but clicking opens the full panel (tier walk, build-up
 * waterfall, margin footer w/ below-floor badge) instead of a mini popover.
 * Persisted detail-page lines pass `snapshot` — the panel then labels the
 * trace as the frozen, never-silently-repriced snapshot (plan §S9.2).
 */

import { useState } from 'react';
import { Typography, theme } from 'antd';
import { AuditOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import PriceTracePanel from '@/components/common/PriceTracePanel';
import type { PriceResolution } from '@/types/api/pricing';
import { formatAmount } from './soFormat';

const { Text } = Typography;

export interface PriceTraceTriggerProps {
  /** The immutable resolver snapshot the affordance explains. */
  resolution: PriceResolution;
  /** Transacted unit price (differs from the resolved one when overridden). */
  unitPrice: number | null;
  /** Display currency (panel fallback when the resolution carries none). */
  currency: string;
  /** Manual-override reason; presence marks the line as overridden. */
  overrideReason?: string | null;
  /** Persisted SO-line snapshot (detail page) vs live draft resolution. */
  snapshot?: boolean;
  /**
   * Minimum margin to judge the footer against: the order's SNAPSHOTTED floor
   * on the detail page, the live company floor on create/edit. Omit to let the
   * panel read the store; null = no floor.
   */
  marginFloorPct?: number | null;
  /** The ORDER HEADER currency — what the panel judges comparability from. */
  documentCurrency?: string | null;
}

const PriceTraceTrigger = ({
  resolution,
  unitPrice,
  currency,
  overrideReason,
  snapshot,
  marginFloorPct,
  documentCurrency,
}: PriceTraceTriggerProps) => {
  const { t } = useTranslation(['orders']);
  const { token } = theme.useToken();
  const [open, setOpen] = useState(false);

  const overridden = Boolean(overrideReason);

  return (
    <>
      <span
        role="button"
        tabIndex={0}
        aria-label={t('orders:so.trace.open')}
        className="tw-inline-flex tw-items-center tw-gap-1"
        style={{ cursor: 'pointer' }}
        onClick={(e) => {
          e.stopPropagation();
          setOpen(true);
        }}
        onKeyDown={(e) => {
          e.stopPropagation();
          if (e.key === 'Enter' || e.key === ' ') setOpen(true);
        }}
      >
        <Text
          style={{
            fontVariantNumeric: 'tabular-nums',
            borderBottom: `1px dashed ${token.colorPrimary}`,
            color: overridden ? token.colorWarning : token.colorText,
          }}
        >
          {unitPrice !== null ? formatAmount(unitPrice) : t('orders:so.trace.unresolved')}
        </Text>
        <AuditOutlined style={{ color: token.colorPrimary, fontSize: token.sizeSM }} />
      </span>

      <PriceTracePanel
        open={open}
        onClose={() => setOpen(false)}
        resolution={resolution}
        unitPrice={unitPrice}
        currency={currency}
        overrideReason={overrideReason}
        snapshot={snapshot}
        marginFloorPct={marginFloorPct}
        documentCurrency={documentCurrency}
      />
    </>
  );
};

export default PriceTraceTrigger;
